import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { RecordSelectionCheckbox } from "@/components/records/RecordSelectionCheckbox";
import { ZoomRecordingControls } from "@/components/session/ZoomRecordingControls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantConfig } from "@/hooks/use-tenant-config";
import { ApiError } from "@/lib/api/client";
import { refreshHomeAfterAct } from "@/lib/home-query-keys";
import {
  sessionRecordingsApi,
  sessionRecordingsQueryKey,
  type RecordingHistoryEvent,
  type RecordingNoticeReceipt,
  type RecordingTransitionAction,
  type TeachingRecordingArtifact,
  type TeachingSessionRecordings,
} from "@/lib/api/session-recordings";

/**
 * The recording on the teaching session portal as one tile (sprint decision Q4,
 * ADR 0028's facts unchanged): what the recording is and its state, once, in
 * the head; two inline checks (the notice receipt, the review); the review
 * link at rest; the learner's name for it defaulted from the session ordinal;
 * and one outline "Publish to learners". The time tile holds the page's filled
 * act (ADR 0035, decision 5); states are words, never chips (decision 6).
 */

/** The four facts a destination review asserts (ADR 0028). */
const REVIEW_CONFIRMATION = {
  authenticated: true,
  no_chat: true,
  no_credentials: true,
  no_admin_controls: true,
} as const;

/** The chosen recording's publication state, said once in the act row. */
const STATE_WORD: Record<Exclude<TeachingRecordingArtifact["publication_status"], null | "published">, string> = {
  withheld: "Withheld",
  withdrawn: "Withdrawn from learners",
  superseded: "Replaced for learners",
};

/**
 * The recording's state in the user's words, said once in the tile's head.
 * The provider's own value never renders; a state that is neither available
 * nor still processing (unavailable, expired, deleted) is "Not available".
 */
const PROVIDER_STATE_WORD: Record<string, string | undefined> = {
  available: "Ready",
  processing: "Processing",
};

function providerStateWord(state: string): string {
  return PROVIDER_STATE_WORD[state] ?? "Not available";
}

const EVENT_WORD: Record<RecordingTransitionAction, string> = {
  publish: "Published",
  withhold: "Withheld",
  correct: "Renamed",
  withdraw: "Withdrawn",
  supersede: "Replaced",
};

function supportsPublication(artifact: TeachingRecordingArtifact): boolean {
  return ["mp4", "m4a"].includes(artifact.file_type.toLowerCase());
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * The name learners will see: a placed recording keeps the name it was placed
 * under; an unplaced one defaults to the session ordinal in the band's wording
 * ("Session 9 recording"), or the provider's own label when the session has no
 * ordinal. Never empty.
 */
function defaultLabel(artifact: TeachingRecordingArtifact, classOrdinal: number | null | undefined): string {
  if (artifact.publication_status !== null) return artifact.display_label;
  return classOrdinal != null ? `Session ${classOrdinal} recording` : artifact.display_label;
}

function reviewQueryKey(sessionId: string, identityKey: string, artifactId: string | null) {
  return ["session-recording-review", identityKey, sessionId, artifactId] as const;
}

export function RecordingTile({
  sessionId,
  identityKey,
  projection,
  classOrdinal,
}: {
  sessionId: string;
  identityKey: string;
  projection: TeachingSessionRecordings;
  classOrdinal?: number | null;
}) {
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const { recordingPolicy, name: tenantName } = useTenantConfig();
  const queryKey = sessionRecordingsQueryKey(sessionId, identityKey);
  const supported = projection.artifacts.filter(supportsPublication);
  const unsupported = projection.artifacts.filter((artifact) => !supportsPublication(artifact));
  const [selectedId, setSelectedId] = useState(supported[0]?.artifact_id ?? "");
  const selected = supported.find((artifact) => artifact.artifact_id === selectedId) ?? supported[0];
  const [label, setLabel] = useState(selected ? defaultLabel(selected, classOrdinal) : "");
  const [renaming, setRenaming] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [replacementId, setReplacementId] = useState("");
  const [replacementConfirmed, setReplacementConfirmed] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const withdrawOpener = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [history, setHistory] = useState<RecordingHistoryEvent[] | null>(null);
  const [readbackBlocked, setReadbackBlocked] = useState(false);
  const [publishedNow, setPublishedNow] = useState<string | null>(null);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const conflictRef = useRef<HTMLParagraphElement>(null);

  function chooseArtifact(artifact: TeachingRecordingArtifact | undefined) {
    setSelectedId(artifact?.artifact_id ?? "");
    setLabel(artifact ? defaultLabel(artifact, classOrdinal) : "");
    setRenaming(false);
    setReviewConfirmed(false);
    setReplacementId("");
    setReplacementConfirmed(false);
    setWithdrawing(false);
    setWithdrawReason("");
    setHistory(null);
  }

  function closeWithdraw() {
    setWithdrawing(false);
    setWithdrawReason("");
    withdrawOpener.current?.focus();
  }

  // The chosen recording follows the projection: when it leaves, the next
  // publishable one is chosen by being the only one left.
  useEffect(() => {
    if (supported.some((artifact) => artifact.artifact_id === selectedId)) return;
    chooseArtifact(supported[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projection.artifacts, selectedId]);

  // The review link is at rest: the exact provider destination for the chosen
  // recording is resolved as the tile shows it, so the check it enables needs
  // no step before it. The route resolves an ephemeral provider URL and
  // records nothing (ADR 0028: review is the instructor's explicit act, below).
  const review = useQuery({
    queryKey: reviewQueryKey(sessionId, identityKey, selected?.artifact_id ?? null),
    queryFn: () => sessionRecordingsApi.review(sessionId, selected!.artifact_id),
    enabled: selected !== undefined,
    retry: false,
    staleTime: 0,
  });
  const replacementReview = useQuery({
    queryKey: reviewQueryKey(sessionId, identityKey, replacementId || null),
    queryFn: () => sessionRecordingsApi.review(sessionId, replacementId),
    enabled: replacementId !== "",
    retry: false,
    staleTime: 0,
  });
  // A review that fails or changes takes its confirmation with it: the check
  // asserts a destination the instructor could open.
  useEffect(() => {
    if (!review.isSuccess) setReviewConfirmed(false);
  }, [review.isSuccess, review.dataUpdatedAt]);
  useEffect(() => {
    if (!replacementReview.isSuccess) setReplacementConfirmed(false);
  }, [replacementReview.isSuccess, replacementReview.dataUpdatedAt]);

  async function refreshProjection() {
    const result = await sessionRecordingsApi.get(sessionId);
    if (!active.current) return result;
    queryClient.setQueryData(queryKey, result);
    refreshHomeAfterAct(queryClient);
    setReadbackBlocked(false);
    return result;
  }

  async function recordNotice(status: RecordingNoticeReceipt["status"]) {
    if (!recordingPolicy) return;
    setBusy("notice");
    setMessage(null);
    try {
      await sessionRecordingsApi.recordNotice(sessionId, recordingPolicy.version, status);
      await refreshProjection();
    } catch {
      try {
        await refreshProjection();
        if (!active.current) return;
        setMessage("It is not clear whether the notice was saved. What is saved now is shown above; check it before trying again.");
      } catch {
        if (!active.current) return;
        setReadbackBlocked(true);
        setMessage("It is not clear whether the notice was saved, and the recording could not be reloaded to check. Reload it before trying again.");
      }
    } finally {
      if (active.current) setBusy(null);
    }
  }

  async function transition(action: RecordingTransitionAction, reason?: string) {
    if (!selected) return;
    setBusy(action);
    setMessage(null);
    try {
      const result = await sessionRecordingsApi.transition(sessionId, selected.artifact_id, {
        action,
        revision: selected.revision,
        display_label: label.trim() || defaultLabel(selected, classOrdinal),
        ...(action === "withdraw" ? { reason } : {}),
        ...(action === "supersede" ? { replacement_target_id: replacementId } : {}),
        ...(action === "publish" || action === "correct" || action === "supersede"
          ? { destination_review: REVIEW_CONFIRMATION }
          : {}),
      });
      if (!active.current) return;
      if ("projection" in result) {
        queryClient.setQueryData(queryKey, result.projection);
        refreshHomeAfterAct(queryClient);
      } else {
        try {
          await refreshProjection();
        } catch {
          if (!active.current) return;
          setReadbackBlocked(true);
          setMessage("The change was saved, but the recording could not be reloaded. Reload it before changing anything else.");
          return;
        }
      }
      setReviewConfirmed(false);
      setReplacementConfirmed(false);
      setRenaming(false);
      setWithdrawing(false);
      setWithdrawReason("");
      setHistory(null);
      if (action === "publish") setPublishedNow(selected.artifact_id);
      if (action === "correct") setMessage("Learners see the new name.");
    } catch (error) {
      let readbackSucceeded = true;
      try {
        await refreshProjection();
      } catch {
        readbackSucceeded = false;
      }
      if (!active.current) return;
      if (!readbackSucceeded) setReadbackBlocked(true);
      if (error instanceof ApiError && error.status === 409) {
        setMessage(readbackSucceeded
          ? "This recording changed while you were reviewing it. Your name for it is kept. Check it again before saving."
          : "This recording changed, and it could not be reloaded. Your name for it is kept. Reload it before saving.");
        requestAnimationFrame(() => conflictRef.current?.focus());
      } else {
        setMessage(readbackSucceeded
          ? "It is not clear whether that was saved, so the recording was reloaded. Check it before trying again."
          : "It is not clear whether that was saved, and the recording could not be reloaded. Reload it before trying again.");
      }
    } finally {
      if (active.current) setBusy(null);
    }
  }

  async function showHistory() {
    const placementId = selected?.placement_id;
    if (!placementId) return;
    setBusy("history");
    try {
      const result = await sessionRecordingsApi.history(sessionId, placementId);
      if (!active.current) return;
      setHistory(result);
    } catch {
      if (!active.current) return;
      setMessage("The recording's history could not be loaded.");
    } finally {
      if (active.current) setBusy(null);
    }
  }

  const notice = projection.notice;
  const noticeConfirmed = notice?.status === "confirmed" || notice?.status === "approved_exception";
  const noticeBy = notice
    ? notice.recorded_by === me?.user_id ? " by you" : ""
    : "";
  const noticeMeta = notice === null
    ? null
    : notice.status === "opted_out"
      ? `This session is kept unrecorded${noticeBy}, ${when(notice.recorded_at)}.`
      : notice.status === "approved_exception"
        ? `Approved exception recorded${noticeBy}, ${when(notice.recorded_at)}.`
        : `Confirmed${noticeBy}, ${when(notice.recorded_at)}.`;
  const reviewReady = review.isSuccess;
  const canPublish = selected !== undefined
    && (selected.publication_status === null || selected.publication_status === "withheld")
    && noticeConfirmed && reviewReady && reviewConfirmed;
  const replacementCandidates = supported.filter((artifact) =>
    artifact.artifact_id !== selected?.artifact_id
    && artifact.placement_id === null
    && artifact.provider_state === "available",
  );
  const locked = busy !== null || readbackBlocked;
  const headMeta = selected ? `${selected.file_type} · ${providerStateWord(selected.provider_state)}` : undefined;
  // The four facts the publish command asserts (REVIEW_CONFIRMATION), as the
  // one sentence the instructor ticks.
  const reviewSentence = (what: string) =>
    `I reviewed ${what}, signed in as ${tenantName ?? "Mufid"}: it shows no chat, no passwords or access links, and no host or admin controls.`;

  // Close's Recording part already says "Recording": the section keeps the name without a second heading.
  return (
    <section aria-labelledby="recorded-class-heading" className="grid gap-4">
      <span id="recorded-class-heading" hidden>Recording</span>
      {headMeta && <p className="text-ui tabular-nums text-muted-foreground">{headMeta}</p>}

      {projection.artifacts.length === 0 && (
        <p className="text-ui text-muted-foreground">No recording has come in from Zoom for this session.</p>
      )}

      {supported.length > 1 && (
        <div className="grid gap-2">
          <p id={`recording-choices-${sessionId}`} className="text-ui font-medium text-foreground">Choose a recording</p>
          <RadioGroup
            aria-labelledby={`recording-choices-${sessionId}`}
            name={`recording-${sessionId}`}
            value={selected?.artifact_id ?? ""}
            className="gap-1"
            onValueChange={(artifactId) => chooseArtifact(supported.find((candidate) => candidate.artifact_id === artifactId))}
          >
            {supported.map((artifact) => (
              <label key={artifact.artifact_id} htmlFor={`recording-choice-${artifact.artifact_id}`} className="flex cursor-pointer items-start gap-3 rounded-control px-3 py-2 hover:bg-well has-[[data-state=checked]]:bg-primary-wash">
                <RadioGroupItem
                  id={`recording-choice-${artifact.artifact_id}`}
                  value={artifact.artifact_id}
                  className="relative -my-1.5 size-11 border-0 shadow-none before:absolute before:top-1/2 before:left-1/2 before:size-4 before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:border before:border-input"
                />
                <span className="grid min-w-0 flex-1 justify-items-start gap-0.5">
                  <span className="text-ui font-medium text-foreground">{artifact.display_label}</span>
                  <span className="text-meta text-muted-foreground">{artifact.file_type}</span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </div>
      )}

      {projection.artifacts.length > 0 && supported.length === 0 && (
        <p className="text-ui text-muted-foreground">No video or audio recording is available to publish.</p>
      )}

      {unsupported.length > 0 && (
        <ul className="grid gap-1 text-meta text-muted-foreground">
          {unsupported.map((artifact) => (
            <li key={artifact.artifact_id}>
              <span className="text-foreground">{artifact.display_label}</span>
              {" · "}{artifact.file_type} · Not available to learners
            </li>
          ))}
        </ul>
      )}

      {selected && recordingPolicy && (
        <div className="grid justify-items-start gap-3">
          {/* Check 1: the notice receipt (ADR 0028). Checking it records the
              host-confirmed receipt; a recorded receipt is a fact, so the box
              is not unchecked by clicking. */}
          <div className="grid justify-items-start gap-1">
            <label htmlFor={`recording-notice-${sessionId}`} className="flex cursor-pointer items-start gap-2 text-ui text-foreground">
              <RecordSelectionCheckbox
                id={`recording-notice-${sessionId}`}
                className="-my-3"
                checked={noticeConfirmed}
                disabled={locked}
                aria-describedby={notice ? `recording-notice-meta-${sessionId}` : undefined}
                onCheckedChange={(checked) => {
                  if (checked !== true || noticeConfirmed) return;
                  void recordNotice("confirmed");
                }}
              />
              <span>Learners were told this session is recorded</span>
            </label>
            <p id={`recording-notice-meta-${sessionId}`} className="text-meta text-muted-foreground">
              {noticeMeta ?? recordingPolicy.class_notice}
            </p>
            {notice?.status !== "opted_out" && notice?.status !== "approved_exception" && (
              <details className="group text-meta">
                <summary className="flex min-h-11 cursor-pointer list-none items-center gap-1.5 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline [&::-webkit-details-marker]:hidden">
                  <ChevronRight aria-hidden strokeWidth={1.75} className="size-4 shrink-0 transition-transform group-open:rotate-90 motion-reduce:transition-none" />
                  Not recording this session?
                </summary>
                <div className="grid justify-items-start gap-3 border-l border-rule pl-4 pt-1">
                  <p className="text-muted-foreground">
                    If anyone raises a concern about being recorded, pause or stop the recording and contact{" "}
                    <a className="inline-flex min-h-11 items-center underline underline-offset-4" href={`mailto:${recordingPolicy.support_email}`}>{recordingPolicy.support_email}</a>.
                    Do not write a private reason here.
                  </p>
                  <Button type="button" size="sm" variant="outline" disabled={locked} onClick={() => void recordNotice("opted_out")}>Keep this session unrecorded</Button>
                  <p className="text-muted-foreground">
                    Use this only for an exception the school already approved. This notes it for this session; it does not approve one.
                  </p>
                  <Button type="button" size="sm" variant="outline" disabled={locked} onClick={() => void recordNotice("approved_exception")}>Record approved exception</Button>
                </div>
              </details>
            )}
          </div>

          {/* Check 2: the review. The exact destination is a link at rest;
              the check is live once it could be opened, and its one sentence
              names the four facts the publish command asserts. */}
          <div className="grid justify-items-start gap-1">
            <ReviewLink review={review} idle="Finding the recording in Zoom" />
            <label htmlFor={`recording-review-confirmed-${sessionId}`} className="flex cursor-pointer items-start gap-2 text-ui text-foreground">
              <RecordSelectionCheckbox
                id={`recording-review-confirmed-${sessionId}`}
                className="-my-3"
                checked={reviewConfirmed}
                disabled={!reviewReady || locked}
                onCheckedChange={(checked) => setReviewConfirmed(checked === true)}
              />
              <span>{reviewSentence("it")}</span>
            </label>
          </div>

          {/* The name learners see, defaulted; a quiet rename reveals the field. */}
          <div className="grid w-full justify-items-start gap-2">
            <p className="text-ui text-foreground">
              {selected.publication_status === "published" ? "Learners see it as " : "Learners will see it as "}
              <strong className="font-semibold">{label.trim() || defaultLabel(selected, classOrdinal)}</strong>
              {!renaming && (selected.publication_status === null || selected.publication_status === "withheld" || selected.publication_status === "published") && (
                <>
                  {" · "}
                  <Button type="button" variant="link" size="sm" className="min-w-[var(--control-height-compact)] px-0" disabled={locked} onClick={() => setRenaming(true)}>rename</Button>
                </>
              )}
            </p>
            {renaming && (
              <div className="flex w-full max-w-md flex-wrap items-center gap-2">
                <Input
                  id={`recording-label-${sessionId}`}
                  aria-label="Name learners see"
                  className="flex-1"
                  value={label}
                  autoFocus
                  onChange={(event) => setLabel(event.target.value)}
                />
                {selected.publication_status === "published" && (
                  <Button type="button" variant="outline" disabled={locked || !reviewReady || !reviewConfirmed} onClick={() => void transition("correct")}>
                    Save name
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* The act. Outline: the time tile holds the page's filled act. */}
          <div className="flex flex-wrap items-center gap-3">
            {(selected.publication_status === null || selected.publication_status === "withheld") && (
              <Button type="button" variant="outline" disabled={locked || !canPublish} onClick={() => void transition("publish")}>
                {busy === "publish" && <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />}
                Publish to learners
              </Button>
            )}
            {selected.publication_status === null && (
              <Button type="button" variant="link" size="sm" className="min-w-[var(--control-height-compact)] px-0" disabled={locked} onClick={() => void transition("withhold")}>Withhold</Button>
            )}
            {selected.publication_status === "published" && (
              <>
                <p className="text-ui text-muted-foreground">
                  {publishedNow === selected.artifact_id
                    ? "Published to learners just now"
                    : selected.published_at ? `Published to learners ${when(selected.published_at)}` : "Published to learners"}
                </p>
                <Button
                  ref={withdrawOpener}
                  type="button"
                  variant="link"
                  size="sm"
                  className="min-w-[var(--control-height-compact)] px-0 text-destructive"
                  disabled={locked}
                  aria-expanded={withdrawing}
                  aria-controls={withdrawing ? `recording-withdraw-${sessionId}` : undefined}
                  onClick={() => (withdrawing ? closeWithdraw() : setWithdrawing(true))}
                >
                  Withdraw
                </Button>
              </>
            )}
            {selected.publication_status === "withdrawn" && (
              <p className="text-ui text-muted-foreground">
                {selected.withdrawn_at ? `Withdrawn from learners ${when(selected.withdrawn_at)}` : STATE_WORD.withdrawn}
              </p>
            )}
            {selected.publication_status === "superseded" && (
              <p className="text-ui text-muted-foreground">{STATE_WORD.superseded}</p>
            )}
            {selected.publication_status === "withheld" && (
              <p className="text-ui text-muted-foreground">{STATE_WORD.withheld}</p>
            )}
            {selected.placement_id && (
              <Button type="button" variant="link" size="sm" className="min-w-[var(--control-height-compact)] px-0" disabled={busy !== null} onClick={() => void showHistory()}>History</Button>
            )}
          </div>

          {/* Withdrawing records why (ADR 0028): the reason opens in place,
              with one act; Cancel leaves the recording as it was. */}
          {withdrawing && selected.publication_status === "published" && (
            <div id={`recording-withdraw-${sessionId}`} role="group" aria-label="Withdraw from learners" className="grid w-full min-w-0 gap-3 rounded-control bg-well p-3">
              <p className="text-ui text-foreground">Learners can no longer open it and see that it was withdrawn. The recording stays in Zoom.</p>
              <div className="grid min-w-0 gap-1.5">
                <Label htmlFor={`recording-withdraw-reason-${sessionId}`}>Why are you withdrawing it?</Label>
                <Textarea
                  id={`recording-withdraw-reason-${sessionId}`}
                  rows={3}
                  maxLength={500}
                  required
                  autoFocus
                  value={withdrawReason}
                  aria-describedby={`recording-withdraw-reason-help-${sessionId}`}
                  onChange={(event) => setWithdrawReason(event.target.value)}
                />
                <p id={`recording-withdraw-reason-help-${sessionId}`} className="text-meta text-muted-foreground">
                  Kept with the recording&apos;s history. Learners do not see it.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-destructive-surface hover:text-destructive"
                  disabled={locked || !withdrawReason.trim()}
                  onClick={() => void transition("withdraw", withdrawReason.trim())}
                >
                  {busy === "withdraw" && <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />}
                  Withdraw from learners
                </Button>
                <Button type="button" size="sm" variant="outline" disabled={busy !== null} onClick={closeWithdraw}>Cancel</Button>
              </div>
            </div>
          )}

          {selected.publication_status === "published" && replacementCandidates.length > 0 && (
            <div className="grid w-full justify-items-start gap-2 border-t border-rule pt-3">
              <label htmlFor={`replacement-${sessionId}`} className="text-ui text-foreground">Replace it for learners with</label>
              <NativeSelect
                id={`replacement-${sessionId}`}
                className="max-w-md"
                value={replacementId}
                onChange={(event) => {
                  setReplacementId(event.target.value);
                  setReplacementConfirmed(false);
                }}
              >
                <option value="">Choose a replacement</option>
                {replacementCandidates.map((artifact) => (
                  <option key={artifact.artifact_id} value={artifact.artifact_id}>{artifact.display_label}</option>
                ))}
              </NativeSelect>
              {replacementId && (
                <>
                  <ReviewLink review={replacementReview} idle="Finding the replacement in Zoom" />
                  <label htmlFor={`recording-replacement-confirmed-${sessionId}`} className="flex cursor-pointer items-start gap-2 text-ui text-foreground">
                    <RecordSelectionCheckbox
                      id={`recording-replacement-confirmed-${sessionId}`}
                      className="-my-3"
                      checked={replacementConfirmed}
                      disabled={!replacementReview.isSuccess || locked}
                      onCheckedChange={(checked) => setReplacementConfirmed(checked === true)}
                    />
                    <span>{reviewSentence("the replacement")}</span>
                  </label>
                  <Button type="button" variant="outline" disabled={locked || !replacementReview.isSuccess || !replacementConfirmed} onClick={() => void transition("supersede")}>
                    Replace for learners
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {message && <p ref={conflictRef} tabIndex={-1} role="status" aria-live="polite" className="text-ui text-muted-foreground">{message}</p>}
      {readbackBlocked && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => void refreshProjection().catch(() => setMessage("The recording still could not be reloaded. Nothing can be changed until it is."))}>
            Reload the recording
          </Button>
        </div>
      )}

      {history && (
        <section aria-labelledby="recording-history-heading" className="grid gap-1">
          <h4 id="recording-history-heading" className="text-ui font-medium text-foreground">History</h4>
          <ol className="grid gap-1 text-ui text-muted-foreground">
            {history.map((event) => (
              <li key={event.event_no}>
                {EVENT_WORD[event.action]} {when(event.created_at)}
                {event.reason && <span className="block text-foreground">Why: {event.reason}</span>}
              </li>
            ))}
          </ol>
        </section>
      )}

      <ZoomRecordingControls sessionId={sessionId} onRead={() => void queryClient.invalidateQueries({ queryKey })} />
    </section>
  );
}

/** The exact review destination as a quiet link at rest, or why it is not. */
function ReviewLink({
  review,
  idle,
}: {
  review: {
    isPending: boolean;
    isSuccess: boolean;
    isError: boolean;
    data?: { url: string; label: string };
    refetch: () => unknown;
  };
  idle: string;
}) {
  if (review.isPending) {
    return (
      <p className="flex min-h-11 items-center gap-2 text-ui text-muted-foreground">
        <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />
        {idle}
      </p>
    );
  }
  if (review.isError || !review.data) {
    return (
      <p role="status" className="flex min-h-11 flex-wrap items-center gap-x-2 text-ui text-muted-foreground">
        The recording could not be opened from Zoom.
        <Button type="button" variant="link" size="sm" className="min-w-[var(--control-height-compact)] px-0" onClick={() => void review.refetch()}>Try again</Button>
      </p>
    );
  }
  return (
    <p className="flex flex-wrap items-center gap-x-2 text-ui">
      <Button asChild variant="link" size="sm" className="min-w-[var(--control-height-compact)] px-0 has-[>svg]:px-0">
        <a href={review.data.url} target="_blank" rel="noopener noreferrer">
          Open the recording
          <ExternalLink aria-hidden className="size-4" strokeWidth={1.75} />
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </Button>
      <span className="text-meta text-muted-foreground">opens Zoom in a new tab</span>
    </p>
  );
}
